import { useEffect, useState } from 'react'
import { usePioneerStore } from '../store/pioneerStore'
import { EightStar, EngravingDivider, GoldButton, SeatCounter, ScreenWrapper } from '../components/shared'

// Orbiting constellation rings (pure CSS / inline SVG decoration)
const ConstellationRings = () => (
  <div
    aria-hidden
    style={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'none',
      overflow: 'hidden',
    }}
  >
    {/* Outer ring */}
    <svg
      width="900"
      height="900"
      viewBox="0 0 900 900"
      style={{
        position: 'absolute',
        opacity: 0.08,
        animation: 'slowSpin 120s linear infinite',
      }}
    >
      <circle cx="450" cy="450" r="420" fill="none" stroke="#C9A34A" strokeWidth="0.5" strokeDasharray="4 12" />
      <circle cx="450" cy="450" r="340" fill="none" stroke="#C9A34A" strokeWidth="0.5" strokeDasharray="2 20" />
      {/* Radial tick marks */}
      {Array.from({ length: 24 }).map((_, i) => {
        const angle = (i * 15 * Math.PI) / 180
        const x1 = 450 + 410 * Math.cos(angle)
        const y1 = 450 + 410 * Math.sin(angle)
        const x2 = 450 + 425 * Math.cos(angle)
        const y2 = 450 + 425 * Math.sin(angle)
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#C9A34A" strokeWidth="1" />
      })}
    </svg>

    {/* Middle ring */}
    <svg
      width="600"
      height="600"
      viewBox="0 0 600 600"
      style={{
        position: 'absolute',
        opacity: 0.06,
        animation: 'slowSpinReverse 80s linear infinite',
      }}
    >
      <circle cx="300" cy="300" r="280" fill="none" stroke="#C9A34A" strokeWidth="0.5" />
      {Array.from({ length: 12 }).map((_, i) => {
        const angle = (i * 30 * Math.PI) / 180
        const x = 300 + 280 * Math.cos(angle)
        const y = 300 + 280 * Math.sin(angle)
        return <circle key={i} cx={x} cy={y} r="3" fill="#C9A34A" />
      })}
    </svg>
  </div>
)

// Animated number counter
const AnimatedNumber = ({ value }: { value: number }) => {
  const [displayed, setDisplayed] = useState(888)

  useEffect(() => {
    const duration = 1400
    const start = 888
    const end = value
    const startTime = performance.now()

    const tick = (now: number) => {
      const elapsed = now - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setDisplayed(Math.round(start + (end - start) * eased))
      if (progress < 1) requestAnimationFrame(tick)
    }

    const id = setTimeout(() => requestAnimationFrame(tick), 600)
    return () => clearTimeout(id)
  }, [value])

  return <span>{displayed}</span>
}

// Props passed by GP1 App.tsx
interface Screen1Props {
  remainingSeats?: number
  totalSeats?: number
  seatsLoading?: boolean
  onCTAClick?: () => void
}

export default function Screen1Landing({
  remainingSeats: remainingSeatsFromApp,
  onCTAClick,
}: Screen1Props) {
  const { goNext } = usePioneerStore()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80)
    return () => clearTimeout(t)
  }, [])

  // Use prop from App.tsx (which calls useRemainingSeats) or fall back to 888
  const remainingSeats = remainingSeatsFromApp ?? 888
  const claimed = 888 - remainingSeats

  return (
    <ScreenWrapper>
      <ConstellationRings />

      <div
        className="relative flex flex-col items-center justify-center min-h-screen px-6 py-16 text-center"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.8s ease, transform 0.8s ease',
        }}
      >
        {/* Top eyebrow */}
        <div
          className="flex items-center gap-3 mb-8"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(-10px)',
            transition: 'opacity 0.8s ease 0.1s, transform 0.8s ease 0.1s',
          }}
        >
          <EightStar size={10} />
          <span
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: '0.65rem',
              letterSpacing: '0.25em',
              color: '#C9A34A',
              textTransform: 'uppercase',
            }}
          >
            Founding Pioneer Registration
          </span>
          <EightStar size={10} />
        </div>

        {/* INFINI wordmark */}
        <div
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 0.9s ease 0.2s, transform 0.9s ease 0.2s',
          }}
        >
          {/* Eight-star above wordmark */}
          <div className="flex justify-center mb-4">
            <EightStar
              size={36}
              color="#C9A34A"
              className="animate-pulse"
            />
          </div>

          <h1
            style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 'clamp(4.5rem, 14vw, 10rem)',
              fontWeight: 700,
              lineHeight: 0.9,
              letterSpacing: '-0.01em',
              background: 'linear-gradient(180deg, #E8C870 0%, #C9A34A 40%, #7A6230 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            INFINI
          </h1>
        </div>

        {/* Tagline */}
        <div
          style={{
            marginTop: '2rem',
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 0.9s ease 0.35s, transform 0.9s ease 0.35s',
          }}
        >
          <p
            style={{
              fontFamily: '"Playfair Display", serif',
              fontStyle: 'italic',
              fontSize: 'clamp(1.2rem, 3vw, 1.8rem)',
              color: '#F5E6C8CC',
              lineHeight: 1.4,
              maxWidth: 540,
            }}
          >
            Your cosmic identity has always existed.
            <br />
            Now it has a name.
          </p>
        </div>

        <EngravingDivider className="w-full max-w-xs my-8" />

        {/* Seat counter */}
        <div
          style={{
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.9s ease 0.5s',
          }}
        >
          <SeatCounter remaining={remainingSeats} />
        </div>

        {/* Body copy */}
        <p
          style={{
            marginTop: '2rem',
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '0.95rem',
            color: '#F5E6C888',
            lineHeight: 1.7,
            maxWidth: 420,
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.9s ease 0.6s',
          }}
        >
          <AnimatedNumber value={claimed} /> people have already claimed their place in the cosmos.
          Only <strong style={{ color: '#C9A34A' }}>{remainingSeats} Pioneer seats remain</strong> — forever.
          Yours is written in the stars. Claim it.
        </p>

        {/* CTA */}
        <div
          style={{
            marginTop: '2.5rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem',
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(12px)',
            transition: 'opacity 0.9s ease 0.7s, transform 0.9s ease 0.7s',
          }}
        >
          <GoldButton size="lg" onClick={onCTAClick ?? goNext}>
            Discover Your Chart
          </GoldButton>
          <span
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: '0.65rem',
              letterSpacing: '0.15em',
              color: '#C9A34A55',
              textTransform: 'uppercase',
            }}
          >
            Free to begin. No account required.
          </span>
        </div>

        {/* Three pillars */}
        <div
          className="grid grid-cols-3 gap-6 mt-16 max-w-lg w-full"
          style={{
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.9s ease 0.9s',
          }}
        >
          {[
            { icon: '☉', label: 'Sun Sign', sub: 'Your core self' },
            { icon: '☽', label: 'Moon Sign', sub: 'Your inner world' },
            { icon: '↑', label: 'Rising Sign', sub: 'Your outer face' },
          ].map(({ icon, label, sub }) => (
            <div key={label} className="flex flex-col items-center gap-1">
              <span
                style={{
                  fontSize: '1.5rem',
                  color: '#C9A34A',
                  fontFamily: 'serif',
                }}
              >
                {icon}
              </span>
              <span
                style={{
                  fontFamily: '"DM Mono", monospace',
                  fontSize: '0.6rem',
                  letterSpacing: '0.15em',
                  color: '#C9A34A',
                  textTransform: 'uppercase',
                }}
              >
                {label}
              </span>
              <span
                style={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: '0.75rem',
                  color: '#F5E6C844',
                }}
              >
                {sub}
              </span>
            </div>
          ))}
        </div>

        {/* Footer note */}
        <p
          style={{
            position: 'absolute',
            bottom: '2rem',
            fontFamily: '"DM Mono", monospace',
            fontSize: '0.6rem',
            letterSpacing: '0.12em',
            color: '#C9A34A33',
            textTransform: 'uppercase',
          }}
        >
          Powered by Swiss Ephemeris · Precise to the arcminute
        </p>
      </div>
    </ScreenWrapper>
  )
}
